import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Stripe as CapacitorStripe } from '@capacitor-community/stripe';
import { Stripe } from '@stripe/stripe-js';
import { StripeService } from 'ngx-stripe';
import { Observable, of, shareReplay, switchMap, take, throwError } from 'rxjs';

import { environment } from '@env/environment';

export const capacitorStripeBridge = {
  isNativePlatform: (): boolean => Capacitor.isNativePlatform(),
  initialize: (options: { publishableKey: string }): Promise<void> =>
    CapacitorStripe.initialize(options),
};

/**
 * Lazily loads Stripe.js (and Capacitor Stripe on native) on first demand.
 * NgxStripeModule must be registered with forRoot() without a publishable key.
 */
@Injectable({
  providedIn: 'root',
})
export class StripeLoaderService {
  private readonly stripeService = inject(StripeService);

  private keyApplied = false;
  private capacitorInitialized = false;
  private ready$?: Observable<Stripe>;

  /** True once changeKey has been applied (script may still be loading). */
  get isKeyApplied(): boolean {
    return this.keyApplied;
  }

  /**
   * Applies the publishable key (once) and emits when the Stripe.js instance is ready.
   * Safe to call multiple times — subsequent calls share the same in-flight load.
   */
  ensureStripeReady$(): Observable<Stripe> {
    if (!this.ready$) {
      this.ready$ = of(null).pipe(
        switchMap(() => {
          this.applyPublishableKey();
          this.initializeCapacitorStripe();

          const stripeInstance = this.stripeService.stripe;
          if (!stripeInstance) {
            return throwError(
              () => new Error('Stripe instance missing after applying publishable key'),
            );
          }

          return stripeInstance.stripe.pipe(take(1));
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.ready$;
  }

  /** Applies the key without waiting for the script (idempotent). */
  applyPublishableKey(): void {
    if (this.keyApplied) {
      return;
    }

    this.stripeService.changeKey(environment.api.stripe.key);
    this.keyApplied = true;
  }

  private initializeCapacitorStripe(): void {
    if (this.capacitorInitialized || !capacitorStripeBridge.isNativePlatform()) {
      return;
    }

    this.capacitorInitialized = true;
    void capacitorStripeBridge.initialize({
      publishableKey: environment.api.stripe.key,
    });
  }
}
